import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  getStopouts,
  deleteStopoutTicker,
  promoteStopoutToReadylist,
  promoteStopoutToLeaders,
  getTickerNotes
} from '../services/api';
import CategorySelect from '../components/CategorySelect';
import { Alert, Button, Spinner } from 'react-bootstrap';
import './Stopouts.css';
import { FaArrowUp, FaStar, FaTrash } from 'react-icons/fa';
import NotesModal from '../components/NotesModal';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  return currencyFormatter.format(Number(value));
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '—';
  return percentFormatter.format(Number(value) / 100);
};

const formatCompactDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (num) => String(num).padStart(2, '0');
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${month}/${day} ${hours}:${minutes}`;
};

const Stopouts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [tickerFilter, setTickerFilter] = useState('');
  const [timeframe, setTimeframe] = useState('today');
  const [sortConfig, setSortConfig] = useState({ key: 'triggered', direction: 'desc' });

  const notesDefaultY = useMemo(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    const tableEl = document.querySelector('.stopouts-table');
    if (tableEl && tableEl.getBoundingClientRect) {
      const rect = tableEl.getBoundingClientRect();
      return Math.max(20, Math.round(rect.top + window.scrollY));
    }
    return null;
    // items.length isn't read directly, but it's an intentional recompute trigger: adding or
    // removing rows changes the table's layout/height, which shifts .stopouts-table's position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const notesDefaultX = useMemo(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    const tableEl = document.querySelector('.stopouts-table');
    if (tableEl && tableEl.getBoundingClientRect) {
      const rect = tableEl.getBoundingClientRect();
      const gap = 12;
      return Math.round(rect.right + window.scrollX + gap);
    }
    return null;
    // Same rationale as notesDefaultY above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesModalNotes, setNotesModalNotes] = useState([]);
  const [notesModalStopout, setNotesModalStopout] = useState(null);

  const compareValues = useCallback((aVal, bVal) => {
    if (aVal === null || aVal === undefined) {
      return bVal === null || bVal === undefined ? 0 : 1;
    }
    if (bVal === null || bVal === undefined) {
      return -1;
    }
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal);
    }
    const numA = Number(aVal);
    const numB = Number(bVal);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      if (numA < numB) return -1;
      if (numA > numB) return 1;
      return 0;
    }
    return 0;
  }, []);

  const getSortValue = useCallback((item, key) => {
    switch (key) {
      case 'ticker':
        return item.ticker?.toUpperCase() || '';
      case 'account':
        return item.account_name?.toUpperCase() || '';
      case 'triggered': {
        const date = new Date(item.triggered_at || item.created_at);
        return Number.isNaN(date.getTime()) ? null : date.getTime();
      }
      case 'shield':
        return item.shield_price !== null && item.shield_price !== undefined
          ? Number(item.shield_price)
          : null;
      case 'gain_pct':
        return item.gain_pct !== null && item.gain_pct !== undefined
          ? Number(item.gain_pct)
          : null;
      case 'category':
        return item.category?.toUpperCase() || '';
      case 'notes': {
        const note = (item.notes && item.notes.length > 0) ? item.notes[0] : null;
        const timestamp = note?.updated_at || note?.created_at || item.latest_note_updated_at;
        if (!timestamp) return null;
        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? null : date.getTime();
      }
      case 'actions': {
        const date = new Date(item.triggered_at || item.created_at);
        return Number.isNaN(date.getTime()) ? null : date.getTime();
      }
      default:
        return item.id || 0;
    }
  }, []);

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'triggered' ? 'desc' : 'asc' };
    });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const renderSortableHeader = (label, key, align = 'left') => {
    const textAlign = align === 'right' ? 'right' : align === 'center' ? 'center' : 'left';
    return (
      <th className={textAlign === 'right' ? 'text-end' : textAlign === 'center' ? 'text-center' : ''}>
        <button
          type="button"
          className="stopouts-sort-btn btn btn-link p-0 text-decoration-none w-100"
          onClick={() => handleSort(key)}
          style={{ color: 'inherit', fontWeight: 600, textAlign }}
        >
          {label}
          <span aria-hidden="true">{renderSortIndicator(key)}</span>
        </button>
      </th>
    );
  };
  const updateStopoutsBadge = useCallback((count) => {
    try {
      localStorage.setItem('stopouts_count', String(count));
    } catch (err) {
      // ignore storage errors
    }
    window.dispatchEvent(new CustomEvent('stopouts:badge', { detail: { count } }));
  }, []);

  const fetchStopouts = useCallback(async () => {
    setRefreshing(true);
    setError('');
    try {
      const response = await getStopouts();
      const data = response.data || [];
      setItems(data);
    } catch (err) {
      setItems([]);
      setError(err.response?.data?.error || 'Failed to load stopouts.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

useEffect(() => {
  fetchStopouts();
}, [fetchStopouts]);

useEffect(() => {
  const interval = setInterval(() => {
    fetchStopouts();
  }, 60000); // refresh every 60s
  return () => clearInterval(interval);
}, [fetchStopouts]);

  const filteredItems = useMemo(() => {
    let result = [...items];
    if (tickerFilter.trim()) {
      const search = tickerFilter.trim().toUpperCase();
      result = result.filter(item => (item.ticker || '').toUpperCase().includes(search));
    }
    if (timeframe !== 'all') {
      const now = new Date();
      result = result.filter(item => {
        const triggered = new Date(item.triggered_at || item.created_at);
        if (Number.isNaN(triggered.getTime())) return false;
        if (timeframe === 'today') {
          return triggered.toDateString() === now.toDateString();
        }
        const daysBack = timeframe === '2d' ? 2 : timeframe === '3d' ? 3 : 0;
        if (daysBack === 0) return true;
        const cutoff = new Date(now);
        cutoff.setDate(now.getDate() - (daysBack - 1));
        cutoff.setHours(0, 0, 0, 0);
        return triggered >= cutoff;
      });
    }
    return result;
  }, [items, tickerFilter, timeframe]);

  useEffect(() => {
    updateStopoutsBadge(filteredItems.length);
  }, [filteredItems.length, updateStopoutsBadge]);

  const handleDelete = async (id) => {
    setBusyId(id);
    setStatus('');
    try {
      await deleteStopoutTicker(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      setStatus('Stopout removed');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete stopout.');
    } finally {
      setBusyId(null);
    }
  };

  const handlePromote = async (id, destination) => {
    setBusyId(id);
    setStatus('');
    try {
      if (destination === 'readylist') {
        await promoteStopoutToReadylist(id);
        setStatus('Promoted to Readylist');
      } else {
        await promoteStopoutToLeaders(id);
        setStatus('Promoted to Leaders');
      }
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to promote ticker.');
    } finally {
      setBusyId(null);
    }
  };

  const handleCategoryUpdate = (updatedItem) => {
    setItems((prev) =>
      prev.map((entry) => (entry.id === updatedItem.id ? { ...entry, ...updatedItem } : entry))
    );
  };

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      const comparison = compareValues(aValue, bValue);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredItems, getSortValue, sortConfig, compareValues]);

  const renderCategoryCell = (item) => {
    const currentCategory = item.category_id
      ? { category_id: item.category_id, name: item.category }
      : null;
    return (
      <CategorySelect
        tickerId={item.id}
        currentCategory={currentCategory}
        onCategoryUpdate={handleCategoryUpdate}
        isStopouts
      />
    );
  };

  const getTickerIdForStopoutItem = (item) => item?.ticker_id || null;

  const openNotesModal = useCallback(async (item) => {
    if (!item) return;
    setNotesModalStopout(item);
    setNotesModalNotes(item.notes || []);
    setNotesModalOpen(true);
    const tickerId = getTickerIdForStopoutItem(item);
    if (!tickerId) {
      return;
    }
    try {
      const res = await getTickerNotes(tickerId);
      setNotesModalNotes(res.data || []);
    } catch (err) {
      console.error('Failed to load notes for stopout:', err);
    }
  }, []);

  const handleNotesModalClose = useCallback(() => {
    setNotesModalOpen(false);
    setNotesModalStopout(null);
    setNotesModalNotes([]);
  }, []);

  const handleNotesUpdate = useCallback((updatedNotes) => {
    setNotesModalNotes(updatedNotes);
    fetchStopouts();
  }, [fetchStopouts]);

  return (
    <div className="stopouts-page">
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {status && (
        <Alert variant="success" onClose={() => setStatus('')} dismissible>
          {status}
        </Alert>
      )}

      <div className="stopouts-filters mb-3">
        <div className="stopouts-filter-field">
          <label htmlFor="stopoutsTicker">Ticker</label>
          <input
            id="stopoutsTicker"
            type="text"
            className="form-control"
            value={tickerFilter}
            placeholder="Search symbol"
            onChange={(e) => setTickerFilter(e.target.value)}
          />
        </div>
        <div className="stopouts-filter-field">
          <label htmlFor="stopoutsTimeframe">Timeframe</label>
          <select
            id="stopoutsTimeframe"
            className="form-select"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="2d">2 days</option>
            <option value="3d">3 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      <div className="stopouts-content">
        <div className="stopouts-table-container flex-grow-1">
          {loading ? (
            <div className="stopouts-loading">
              <Spinner animation="border" role="status" />
            </div>
          ) : sortedItems.length === 0 ? (
            <div className="stopouts-empty">
              <p className="text-muted mb-1">No stopouts have been recorded yet.</p>
              <small>
                When a stop loss is triggered on any account, it will show up here automatically.
              </small>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle stopouts-table">
                <thead>
                  <tr>
                    {renderSortableHeader('Ticker', 'ticker')}
                    {renderSortableHeader('Account', 'account')}
                    {renderSortableHeader('Triggered', 'triggered')}
                    {renderSortableHeader('Shield', 'shield')}
                    {renderSortableHeader('Gain%', 'gain_pct')}
                    {renderSortableHeader('Category', 'category')}
                    {renderSortableHeader('Notes', 'notes')}
                    {renderSortableHeader('Actions', 'actions', 'right')}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => (
                    <tr
                      key={item.id}
                      className="stopouts-row"
                    >
                      <td className="stopouts-ticker-cell">
                        <div className="fw-bold">{item.ticker}</div>
                      </td>
                      <td className="stopouts-account-cell">
                        <div className="fw-semibold text-muted">
                          {item.account_name || '—'}
                        </div>
                      </td>
                      <td className="stopouts-trigger-cell">
                        <div className="fw-semibold">
                          {formatCompactDate(item.triggered_at || item.created_at)}
                        </div>
                      </td>
                      <td className="stopouts-shield-cell">
                        <div className="fw-bold">{formatCurrency(item.shield_price)}</div>
                      </td>
                  <td>
                    <div className={Number(item.gain_pct) >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                      {formatPercent(item.gain_pct)}
                    </div>
                  </td>
                      <td>{renderCategoryCell(item)}</td>
                      <td
                        className="stopouts-notes-cell"
                        onClick={() => openNotesModal(item)}
                        role="button"
                        style={{ cursor: 'pointer' }}
                      >
                        {item.notes && item.notes.length > 0 ? (
                          <span className="badge bg-success stopouts-note-badge">
                            {formatCompactDate(item.notes[0].updated_at || item.notes[0].created_at)}
                          </span>
                        ) : (
                          <span className="text-muted">Add note</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex justify-content-end align-items-center stopouts-action-row">
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="stopouts-icon-btn"
                            disabled={busyId === item.id}
                            onClick={() => handlePromote(item.id, 'readylist')}
                            title="Promote to Readylist"
                          >
                            <FaArrowUp size="0.8em" />
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="stopouts-icon-btn"
                            disabled={busyId === item.id}
                            onClick={() => handlePromote(item.id, 'leaders')}
                            title="Promote to Leaders"
                          >
                            <FaStar size="0.8em" />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="stopouts-icon-btn"
                            disabled={busyId === item.id}
                            onClick={() => handleDelete(item.id)}
                            title="Remove"
                          >
                            <FaTrash size="0.75em" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {notesModalStopout && (
        <NotesModal
          show={notesModalOpen}
          onHide={handleNotesModalClose}
          ticker={{
            ticker_id: notesModalStopout.ticker_id,
            symbol: notesModalStopout.ticker,
            name: notesModalStopout.company_name
          }}
          notes={notesModalNotes}
          setNotesForTicker={handleNotesUpdate}
          floating={true}
          defaultY={notesDefaultY}
          defaultX={notesDefaultX}
        />
      )}
    </div>
  );
};

export default Stopouts;
